/**
 * Brand tokens — hex source of truth for runtime (canvas, SVG, PDF, inline styles).
 * Keep in sync with src/styles/tokens/primitives.css
 * Source: Aptive Index Brand Styleguide
 */

export const brandLight = {
  paper: '#FAF7F2',
  surface: '#FFFEFD',
  sunken: '#F2EEE7',
  border: '#E6E0D8',
  borderStrong: '#D2CAC0',
  text: '#2E2A28', // ink — CSS: --ink (not --text; --text is the font stack)
  textMuted: '#6E665F', // CSS: --ink-muted

  primary: '#6B4A9E',
  primaryHover: '#5C3F88',
  primarySolid: '#6B4A9E',
  primarySolidH: '#5C3F88',
  primaryOn: '#FFFFFF',
  primaryBorder: '#C1B2D0',
  primaryTint: '#EDE5F5',

  amber: '#E68E43',
  amberText: '#8F5420',
  amberBorder: '#F2CDAC',
  amberTint: '#FBEEDF',

  teal: '#2E8B84',
  tealText: '#24706B',
  tealBorder: '#A8CCC6',
  tealTint: '#E0EFEC',

  clay: '#B94A6E',
  clayText: '#A03D5D',
  clayBorder: '#E0B2BD',
  clayTint: '#F7E4E9',

  /* Signal Trait accents */
  sage: '#6B8E4E',
  sageText: '#4F6B39',
  sageBorder: '#C3D3AF',
  sageTint: '#EBF1E3',

  steel: '#4C74A8',
  steelText: '#385880',
  steelBorder: '#B7C8DE',
  steelTint: '#E5EBF3',

  umber: '#8C5A3C',
  umberText: '#6B4329',
  umberBorder: '#D8BFA8',
  umberTint: '#F2E7DC',

  slate: '#6B6F7D',
  slateText: '#4F5260',
  slateBorder: '#C7C9D0',
  slateTint: '#ECEDF0',

  white: '#FFFFFF',
  black: '#141210',

  g050: '#FCFBF9',
  g100: '#FAF7F2',
  g150: '#F2EEE7',
  g200: '#E6E0D8',
  g250: '#D2CAC0',
  g300: '#B5ADA3',
  g400: '#8F877E',
  g500: '#6E665F',
  g600: '#544D46',
  g700: '#3D3833',
  g750: '#2E2A27',
  g800: '#232019',
  g850: '#1E1B18',
  g900: '#1A1715',

  tierBronze: '#CD7F32',
  tierSilver: '#C0C0C0',
  tierGold: '#FFD700',

  info: '#5C6BC0',
  infoLight: '#E8EAF6',
  infoDark: '#3949AB',
} as const;

export const brandDark = {
  paper: '#1A1715',
  surface: '#232019',
  sunken: '#141210',
  border: '#3D3833',
  borderStrong: '#544D46',
  text: '#F0EBE4',
  textMuted: '#B5ADA3',

  primary: '#A98CDB',
  primaryHover: '#BCA3E6',
  primarySolid: '#7E5FBF',
  primarySolidH: '#8E6FCF',
  primaryOn: '#FFFFFF',
  primaryBorder: '#52465D',
  primaryTint: '#2C2436',

  amber: '#F0A868',
  amberText: '#F0A868',
  amberBorder: '#6B5035',
  amberTint: '#382B1F',

  teal: '#4FB3AA',
  tealText: '#4FB3AA',
  tealBorder: '#32544C',
  tealTint: '#1F332F',

  clay: '#DE7A9B',
  clayText: '#DE7A9B',
  clayBorder: '#644047',
  clayTint: '#38242B',

  sage: '#8FB36C',
  sageText: '#8FB36C',
  sageBorder: '#47563A',
  sageTint: '#2A3322',

  steel: '#7CA0D9',
  steelText: '#7CA0D9',
  steelBorder: '#3D4E68',
  steelTint: '#232D3D',

  umber: '#C08355',
  umberText: '#C08355',
  umberBorder: '#5C4433',
  umberTint: '#362A20',

  slate: '#9CA0AC',
  slateText: '#9CA0AC',
  slateBorder: '#494C56',
  slateTint: '#2B2D33',

  white: '#FFFFFF',
  black: '#141210',

  g100: '#232019',
  g200: '#3D3833',
  g300: '#544D46',
  g400: '#B5ADA3',
  g500: '#B5ADA3',
  g600: '#544D46',
  g700: '#3D3833',
  g800: '#232019',
  g900: '#1A1715',
} as const;

/** ISCP / chart colors (light theme — used by SVG, canvas, PDF) */
export const chartColors = {
  influence: brandLight.primary,
  sociability: brandLight.amber,
  consistency: brandLight.teal,
  precision: brandLight.clay,
  connectingLine: brandLight.g400,
  midpointLineMini: brandLight.g400,
  midpointLine: brandLight.g700,
  sigmaLine: brandLight.g200,
  greyLite: brandLight.g100,
  /** Approx primary 60% white */
  targetRange: '#B5A0D4',
  /** Approx primary 80% black */
  targetEdit: '#3F2C5E',
  targetHandles: '#B5A0D4',
  inColor: brandLight.umber,
  erColor: brandLight.sage,
  siColor: brandLight.umber,
  psColor: brandLight.steel,
  abColor: brandLight.slate,
  greyC: brandLight.g200,
  enhanced: brandLight.teal,
  lens: '#A08BC8',
  reasoningApproach: '#A08BC8',
  connectionStyle: '#A08BC8',
  executionStyle: '#A08BC8',
  er: brandLight.sage,
  ps: brandLight.steel,
  ab: brandLight.slate,
  in: brandLight.umber,
} as const;

export type BrandLight = typeof brandLight;
export type ChartColors = typeof chartColors;

export default chartColors;
